import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Cpu, Leaf } from "lucide-react";
import Uploader from "./Uploader";
import ProductCard from "./ProductCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCAN_IMG =
  "https://static.prod-images.emergentagent.com/jobs/4c60559f-0b77-4356-af53-e037885af13d/images/0dd9bee6a98340e611a58a40d686f7895d9be8511ae65ab7f5578958a7e48b97.png";

// German display labels for English enum values returned by the AI.
const DE = {
  hair_type: { straight: "Glatt", wavy: "Wellig", curly: "Lockig", coily: "Kraus" },
  texture:   { fine: "Fein", medium: "Mittel", coarse: "Grob" },
  density:   { low: "Niedrig", medium: "Mittel", high: "Hoch" },
  condition: { excellent: "Ausgezeichnet", good: "Gut", fair: "Mittelmäßig", damaged: "Beschädigt" },
  scalp:     { normal: "Normal", dry: "Trocken", oily: "Fettig", sensitive: "Empfindlich", flaky: "Schuppig" },
  category:  {
    "anti-hairloss": "Anti-Haarausfall",
    "hair-repair": "Haarreparatur",
    "colour-care": "Farbpflege",
    "hair-follicle-strength": "Haarfollikel-Stärkung",
    "hair-growth-and-repair": "Haarwachstum & Reparatur",
    "hair-growth": "Haarwachstum",
    all: "Alle",
  },
};
const tr = (group, value) => DE[group]?.[String(value || "").toLowerCase()] || value;

function DiagnoseSection({
  images, setImages, status, onAnalyze, progress, result, onAdd,
}) {
  const canAnalyze = images.length >= 1 && status !== "loading";

  return (
    <section
      id="diagnose"
      className="py-16 lg:py-24 bg-[var(--brand-bg-2)] relative overflow-hidden"
      data-testid="diagnose-section"
    >
      <div className="max-w-6xl mx-auto px-6">
        <div className="max-w-2xl">
          <p className="text-[11px] tracking-[0.3em] uppercase text-[var(--brand-gold-dark)]">Schritt 01 · Diagnose</p>
          <h2 className="mt-3 font-serif-display text-4xl sm:text-5xl leading-[1.05]">
            Lade deine Fotos hoch. Erhalte eine professionelle Diagnose.
          </h2>
          <p className="mt-5 text-[var(--brand-ink-soft)]">
            Gutes Licht. Sauberes, trockenes Haar. Eine Nahaufnahme von Kopfhaut, Längen und Spitzen.
            Wir analysieren – und speichern Ihre Bilder niemals.
          </p>
        </div>

        <div className="mt-10 grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-3">
            <Uploader images={images} setImages={setImages} disabled={status === "loading"} />

            <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
              <p className="text-sm text-[var(--brand-ink-soft)]" data-testid="upload-counter">
                {images.length} / 5 Fotos ausgewählt
              </p>
              <button
                type="button"
                onClick={onAnalyze}
                disabled={!canAnalyze}
                data-testid="analyze-btn"
                className="btn-gold px-8 py-4 text-xs tracking-[0.25em] uppercase inline-flex items-center gap-2"
              >
                <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                {status === "loading" ? "Analysiere…" : "Mein Haar analysieren"}
              </button>
            </div>
          </div>

          <aside
            className="lg:col-span-2 bg-white border hairline p-6 relative overflow-hidden"
            data-testid="diagnose-preview"
          >
            {status === "idle" && !result && (
              <div className="relative aspect-square overflow-hidden">
                <img src={SCAN_IMG} alt="KI-Analyse" className="w-full h-full object-cover" />
                <div className="absolute inset-0 flex items-end p-4">
                  <p className="text-white/95 font-serif-display text-2xl leading-tight drop-shadow">
                    Jede Strähne, verstanden.
                  </p>
                </div>
              </div>
            )}
            {status === "loading" && (
              <div className="relative aspect-square overflow-hidden" data-testid="analyzing-state">
                <img src={SCAN_IMG} alt="" className="w-full h-full object-cover opacity-80" />
                <div className="scan-line" />
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-black/20">
                  <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center pulse-ring">
                    <Cpu className="w-6 h-6 text-[var(--brand-gold-dark)]" strokeWidth={1.5} />
                  </div>
                  <p className="text-white font-serif-display text-xl">Wir lesen dein Haar…</p>
                  <div className="w-60 h-1 bg-white/30 overflow-hidden">
                    <div
                      className="h-full bg-[var(--brand-gold)] transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <p className="text-white/80 text-[11px] tracking-[0.25em] uppercase">{progress}%</p>
                </div>
              </div>
            )}
            {status === "done" && result && (
              <div data-testid="analysis-summary-card">
                <p className="text-[11px] tracking-[0.3em] uppercase text-[var(--brand-gold-dark)]">Deine Diagnose</p>
                <h3 className="font-serif-display text-2xl mt-2">
                  {tr("hair_type", result.analysis.hair_type)} · {tr("texture", result.analysis.texture)}
                </h3>
                <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <dt className="text-[var(--brand-ink-soft)]">Dichte</dt>
                  <dd>{tr("density", result.analysis.density)}</dd>
                  <dt className="text-[var(--brand-ink-soft)]">Zustand</dt>
                  <dd>{tr("condition", result.analysis.condition)}</dd>
                  <dt className="text-[var(--brand-ink-soft)]">Kopfhaut</dt>
                  <dd>{tr("scalp", result.analysis.scalp)}</dd>
                </dl>
                <p className="mt-4 text-sm italic text-[var(--brand-ink-soft)] border-l-2 border-[var(--brand-gold)] pl-3">
                  {result.analysis.summary}
                </p>
              </div>
            )}
          </aside>
        </div>

        {status === "done" && result && (
          <div className="mt-20" data-testid="analysis-result">
            <div className="grid lg:grid-cols-3 gap-10">
              <div className="lg:col-span-1">
                <p className="text-[11px] tracking-[0.3em] uppercase text-[var(--brand-gold-dark)]">Schritt 02 · Diagnose</p>
                <h3 className="mt-3 font-serif-display text-3xl">Was wir sehen.</h3>
                <div className="mt-6 space-y-5 text-sm">
                  <div>
                    <div className="text-[var(--brand-ink-soft)] uppercase tracking-widest text-[11px]">Anliegen</div>
                    <ul className="mt-2 space-y-1" data-testid="concerns-list">
                      {result.analysis.concerns.map((c) => (
                        <li key={c} className="flex items-start gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-[var(--brand-wine)] mt-2" />
                          {c}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className="text-[var(--brand-ink-soft)] uppercase tracking-widest text-[11px]">Stärken</div>
                    <ul className="mt-2 space-y-1" data-testid="strengths-list">
                      {result.analysis.strengths.map((c) => (
                        <li key={c} className="flex items-start gap-2">
                          <Leaf className="w-3.5 h-3.5 mt-1 text-[var(--brand-gold-dark)]" strokeWidth={1.5} />
                          {c}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className="text-[var(--brand-ink-soft)] uppercase tracking-widest text-[11px]">Empfohlene Pflege</div>
                    <div className="mt-2 flex flex-wrap gap-2" data-testid="categories-list">
                      {result.analysis.recommended_categories.map((c) => (
                        <span key={c} className="text-[11px] tracking-widest uppercase border hairline px-2.5 py-1">
                          {tr("category", c)}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-2">
                <p className="text-[11px] tracking-[0.3em] uppercase text-[var(--brand-gold-dark)]">Schritt 03 · Pflegeplan</p>
                <h3 className="mt-3 font-serif-display text-3xl">Deine individuelle Auswahl.</h3>
                <div className="mt-6 grid sm:grid-cols-2 gap-6" data-testid="recommendations-grid">
                  {result.recommendations.map((p) => (
                    <ProductCard key={p.id} product={p} onAdd={onAdd} showMatch testIdPrefix="reco" />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function Catalog({ products, onAdd }) {
  const [filter, setFilter] = useState("all");
  const cats = useMemo(() => {
    const set = new Set(products.map((p) => p.category));
    return ["all", ...Array.from(set)];
  }, [products]);
  const list = filter === "all" ? products : products.filter((p) => p.category === filter);

  return (
    <section id="catalog" className="py-16 lg:py-24" data-testid="catalog-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <div>
            <p className="text-[11px] tracking-[0.3em] uppercase text-[var(--brand-gold-dark)]">Unser Shop</p>
            <h2 className="mt-3 font-serif-display text-4xl sm:text-5xl leading-[1.05]">
              Die komplette Kollektion.
            </h2>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="catalog-filters">
            {cats.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                data-testid={`filter-${c}`}
                className={`px-3 py-1.5 text-[11px] tracking-[0.2em] uppercase border hairline transition ${
                  filter === c
                    ? "bg-[var(--brand-ink)] text-white border-[var(--brand-ink)]"
                    : "hover:border-[var(--brand-gold)]"
                }`}
              >
                {tr("category", c)}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-6" data-testid="catalog-grid">
          {list.map((p) => (
            <ProductCard key={p.id} product={p} onAdd={onAdd} testIdPrefix="shop" />
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  const [images, setImages] = useState([]);
  const [status, setStatus] = useState("idle"); // idle | loading | done
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/products`)
      .then((r) => setProducts(r.data.products || []))
      .catch(() => toast.error("Katalog konnte nicht geladen werden. Bitte erneut versuchen."));
  }, []);

  useEffect(() => {
    if (status !== "loading") return;
    setProgress(5);
    const t = setInterval(() => {
      setProgress((p) => (p < 92 ? p + Math.max(1, Math.round((92 - p) / 20)) : p));
    }, 400);
    return () => clearInterval(t);
  }, [status]);

  const onAnalyze = async () => {
    if (images.length < 1) {
      toast.error("Bitte füge mindestens ein Foto hinzu.");
      return;
    }
    setStatus("loading");
    setResult(null);
    try {
      const { data } = await axios.post(
        `${API}/analyze-hair`,
        { images: images.map((i) => i.base64) },
        { timeout: 90000 }
      );
      setProgress(100);
      setResult(data);
      setStatus("done");
      toast.success("Deine Diagnose ist bereit.");
      setTimeout(() => {
        document
          .querySelector('[data-testid="analysis-result"]')
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
    } catch (e) {
      setStatus("idle");
      setProgress(0);
      toast.error(
        e?.response?.data?.detail ||
          "Analyse fehlgeschlagen. Bitte versuche es mit klareren, gut beleuchteten Fotos."
      );
    }
  };

  const onAdd = (product) => {
    toast.success(`${product.name} · zur Auswahl hinzugefügt`);
  };

  return (
    <div className="min-h-screen bg-[var(--brand-bg)] text-[var(--brand-ink)]">
      <DiagnoseSection
        images={images}
        setImages={setImages}
        status={status}
        onAnalyze={onAnalyze}
        progress={progress}
        result={result}
        onAdd={onAdd}
      />
      <Catalog products={products} onAdd={onAdd} />
    </div>
  );
}
